import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Image as ImageIcon, FileCode2, AlertCircle, Printer } from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/barcode-generator";
const TITLE = "Online Barcode Generator — UPC, EAN, Code 128 & More";
const DESC =
  "Free online barcode generator. Create UPC-A, EAN-13, Code 128, Code 39, ITF-14 and Codabar barcodes as PNG or SVG — rendered in your browser.";

const FAQ = [
  {
    q: "Which barcode formats can I generate?",
    a: "The generator supports the major retail and logistics 1D symbologies: UPC-A, EAN-13, EAN-8, Code 128, Code 39, ITF-14 and Codabar. For QR codes use the QR Code reader/generator pages.",
  },
  {
    q: "Do I Need to Provide the Check Digit?",
    a: "No. For UPC-A, EAN-13, EAN-8 and ITF-14 the generator will compute and append the correct GS1 mod-10 check digit automatically if you leave it off. You can also enter the full value with the check digit and it will be used as-is.",
  },
  {
    q: "Is My Data Uploaded Anywhere?",
    a: "No. Rendering happens entirely in your browser using bwip-js. The value you type and the PNG/SVG file you download never leave your device.",
  },
  {
    q: "What's the Difference Between PNG and SVG?",
    a: "PNG is a raster image — good for emails, slides and screenshots. SVG is a crisp vector — best for printing labels and packaging because it stays sharp at any size.",
  },
  {
    q: "Can I Use These Barcodes Commercially?",
    a: "Yes. You own the data you encode. To sell products through retailers you still need a GS1-issued company prefix so your UPC/EAN codes are globally unique — this tool only renders the symbol.",
  },
  {
    q: "Are Free Barcode Generators Safe?",
    a: "This one is safe for your data: the barcode is drawn in your browser by the bwip-js library and nothing you type is sent to a server. The bigger risk with any free generator is a barcode that does not scan or a retail number you do not own, so check the number and scan a test print before ordering labels.",
  },
  {
    q: "How Do I Generate a Barcode From a Number?",
    a: "Pick the format that matches the length of the number: 12 digits for UPC-A, 13 for EAN-13, 8 for EAN-8 or 14 for ITF-14, then type it in. Enter it one digit short and the check digit is added; enter it in full and a wrong check digit is flagged. Numbers of any other length can be encoded as Code 128.",
  },
  {
    q: "Are Barcodes Free?",
    a: "Drawing a barcode is free, and Code 128 or Code 39 values for internal use cost nothing. Retail barcodes need a GTIN licensed from GS1, and the fee depends on the country and on how many numbers you need.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Online Barcode Generator", path: PATH },
];

type FormatId = "upca" | "ean13" | "ean8" | "code128" | "code39" | "itf14" | "rationalizedCodabar";

type FormatDef = {
  id: FormatId;
  label: string;
  placeholder: string;
  hint: string;
  /** Optional pre-render validation. Return error message or null. */
  validate?: (v: string) => string | null;
};

const FORMATS: FormatDef[] = [
  {
    id: "upca",
    label: "UPC-A (12 digits)",
    placeholder: "036000291452",
    hint: "11 or 12 digits. Check digit auto-computed if 11 are entered.",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "UPC-A only allows digits 0–9.";
      if (v.length !== 11 && v.length !== 12)
        return `UPC-A requires 11 or 12 digits (you entered ${v.length}).`;
      if (v.length === 12 && computeMod10Check(v.slice(0, 11)) !== v[11])
        return "UPC-A check digit is invalid.";
      return null;
    },
  },
  {
    id: "ean13",
    label: "EAN-13 (13 digits)",
    placeholder: "5901234123457",
    hint: "12 or 13 digits. Check digit auto-computed if 12 are entered.",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "EAN-13 only allows digits 0–9.";
      if (v.length !== 12 && v.length !== 13)
        return `EAN-13 requires 12 or 13 digits (you entered ${v.length}).`;
      if (v.length === 13 && computeMod10Check(v.slice(0, 12)) !== v[12])
        return "EAN-13 check digit is invalid.";
      return null;
    },
  },
  {
    id: "ean8",
    label: "EAN-8 (8 digits)",
    placeholder: "96385074",
    hint: "7 or 8 digits. Check digit auto-computed if 7 are entered.",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "EAN-8 only allows digits 0–9.";
      if (v.length !== 7 && v.length !== 8)
        return `EAN-8 requires 7 or 8 digits (you entered ${v.length}).`;
      if (v.length === 8 && computeMod10Check(v.slice(0, 7)) !== v[7])
        return "EAN-8 check digit is invalid.";
      return null;
    },
  },
  {
    id: "code128",
    label: "Code 128 (alphanumeric)",
    placeholder: "ABC-1234",
    hint: "Any printable ASCII characters. Length is flexible.",
    validate: (v) => {
      if (v.length === 0) return "Enter some data to encode.";
      if (v.length > 80) return `Code 128 is limited to 80 characters (you entered ${v.length}).`;
      if (!/^[\x20-\x7E]+$/.test(v))
        return "Code 128 only allows printable ASCII characters (no tabs, line breaks or non-Latin letters).";
      return null;
    },
  },
  {
    id: "code39",
    label: "Code 39 (uppercase)",
    placeholder: "HELLO-123",
    hint: "Uppercase A–Z, 0–9, and - . $ / + % SPACE.",
    validate: (v) => {
      if (v.length === 0) return "Enter some data to encode.";
      if (v.length > 43) return `Code 39 is limited to 43 characters (you entered ${v.length}).`;
      if (!/^[A-Z0-9 \-.$/+%]+$/.test(v))
        return "Code 39 only allows uppercase A–Z, 0–9 and - . $ / + % SPACE.";
      return null;
    },
  },
  {
    id: "itf14",
    label: "ITF-14 (14 digits)",
    placeholder: "10614141000415",
    hint: "13 or 14 digits. Check digit auto-computed if 13 are entered.",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "ITF-14 only allows digits 0–9.";
      if (v.length !== 13 && v.length !== 14)
        return `ITF-14 requires 13 or 14 digits (you entered ${v.length}).`;
      if (v.length === 14 && computeMod10Check(v.slice(0, 13)) !== v[13])
        return "ITF-14 check digit is invalid.";
      return null;
    },
  },
  {
    id: "rationalizedCodabar",
    label: "Codabar",
    placeholder: "A1234567B",
    hint: "Start/stop char A–D, digits and - $ : / . + in between.",
    validate: (v) => {
      if (v.length === 0) return "Enter some data to encode.";
      if (v.length < 3) return "Codabar needs a start char, data, and a stop char.";
      if (v.length > 60) return `Codabar is limited to 60 characters (you entered ${v.length}).`;
      if (!/^[A-D][0-9\-$:/.+]*[A-D]$/.test(v))
        return "Codabar must start/end with A–D and only contain digits and - $ : / . + in between.";
      return null;
    },
  },
];

function computeMod10Check(digits: string): string {
  // Standard GS1 mod-10 (rightmost data digit multiplied by 3).
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const d = digits.charCodeAt(digits.length - 1 - i) - 48;
    sum += i % 2 === 0 ? d * 3 : d;
  }
  const mod = sum % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

function withCheckDigit(format: FormatId, value: string): string {
  switch (format) {
    case "upca":
      return value.length === 11 ? value + computeMod10Check(value) : value;
    case "ean13":
      return value.length === 12 ? value + computeMod10Check(value) : value;
    case "ean8":
      return value.length === 7 ? value + computeMod10Check(value) : value;
    case "itf14":
      return value.length === 13 ? value + computeMod10Check(value) : value;
    default:
      return value;
  }
}

function safeFilename(format: FormatId, value: string): string {
  const slug = value.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "barcode";
  return `${format}-${slug}`;
}

function BarcodeGeneratorPage() {
  const [format, setFormat] = useState<FormatId>("ean13");
  const [value, setValue] = useState("5901234123457");
  const [printOpen, setPrintOpen] = useState(false);
  const [scale, setScale] = useState(3);
  const [height, setHeight] = useState(15);
  const [includeText, setIncludeText] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState<string>("");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const def = useMemo(() => FORMATS.find((f) => f.id === format)!, [format]);

  const encodedValue = useMemo(() => withCheckDigit(format, value.trim()), [format, value]);

  useEffect(() => {
    const trimmed = value.trim();
    const validationErr = def.validate ? def.validate(trimmed) : null;
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        ctx?.clearRect(0, 0, c.width, c.height);
      }
      return;
    }

    const opts = {
      bcid: format,
      text: encodedValue,
      scale,
      height,
      includetext: includeText,
      textxalign: "center" as const,
      backgroundcolor: "FFFFFF",
    };

    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      try {
        if (canvasRef.current) {
          bwipjs.toCanvas(canvasRef.current, opts);
        }
        const svg = bwipjs.toSVG(opts);
        setSvgMarkup(svg);
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to render barcode.";
        setError(msg);
        setSvgMarkup("");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [format, value, encodedValue, scale, height, includeText, def]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(format, encodedValue)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(format, encodedValue)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Online Barcode Generator"
      description="Create UPC-A, EAN-13, EAN-8, Code 128, Code 39, ITF-14 and Codabar barcodes. Download as PNG or SVG. Everything runs in your browser. Leave off the check digit for UPC-A, EAN-13, EAN-8 or ITF-14 and the generator calculates it, or enter the full number and it checks the digit."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the online barcode generator"
            about={
              <>
                <p>
                  The ReadBarcode online barcode generator turns a value into a print-ready barcode in
                  your browser. It renders UPC-A and EAN-13 for retail, Code 128 and Code 39 for labels,
                  ITF-14 for shipping cartons, EAN-8 for small packaging, and Codabar for libraries and
                  blood banks. Output is a scannable PNG or a vector SVG ready for packaging artwork.
                </p>
                <p>
                  Encoding runs entirely on your device with the bwip-js library — no server, no
                  account, no upload. The check digit is computed for you when the format requires one,
                  so you can paste a 12-digit base GTIN and get a valid EAN-13 back instantly.
                </p>
              </>
            }
            whenToUse={[
              "Mocking up product packaging or labels before a print run.",
              "Generating Code 128 labels for internal inventory or asset tags.",
              "Producing an ITF-14 carton barcode from a known GTIN-14.",
              "Quickly creating a test barcode to verify a scanner setup.",
              "Printing Codabar tickets, library cards or medical wristbands.",
              "Embedding an SVG barcode in a design file at any scale without blur.",
            ]}
            howItWorks={[
              {
                title: "Pick a format",
                desc: "Choose the symbology that matches your use case — UPC-A and EAN-13 for retail, Code 128 for most everything else.",
              },
              {
                title: "Enter the value",
                desc: "Type or paste the data. For UPC/EAN/ITF the check digit is optional — it's calculated automatically.",
              },
              {
                title: "Tune size and text",
                desc: "Adjust the scale, bar height, and toggle the human-readable value under the bars.",
              },
              {
                title: "Preview live",
                desc: "The preview re-renders on every change so you see exactly what will print.",
              },
              {
                title: "Download PNG or SVG",
                desc: "Save the PNG for screens and slides, or the SVG for print and packaging.",
              },
            ]}
            whatToAvoid={[
              "Using a UPC or EAN code you don't own for resale — you need a GS1-issued company prefix.",
              "Printing at less than 80% of the GS1 nominal size — small symbols often fail at retail scanners.",
              "Cropping out the quiet zone (white space) around the bars — scanners rely on it.",
              "Stretching the PNG non-proportionally in a design tool — use the SVG instead.",
              "Encoding lowercase or special characters in Code 39 — it only allows uppercase A–Z, 0–9 and a few symbols.",
            ]}
            tips={[
              "For packaging artwork always export SVG so the bars stay crisp at any size.",
              "If your scanner struggles, increase the scale by 1 and re-test before changing the format.",
              "Use Code 128 for any internal label — it's denser and more reliable than Code 39.",
              "Check the rendered value with the Barcode Reader on this site as a sanity test.",
              "Validate retail GTINs with the GTIN Validator before printing thousands of labels.",
            ]}
          />
          <PrivacyNote />
          <h2>How to Generate a Barcode for Your Product</h2>
          <p>To generate a barcode for a product sold in stores, get a GTIN from GS1, enter it here as a UPC-A or EAN-13 and download the SVG for your packaging artwork.</p>
          <ol>
            <li>License a GS1 Company Prefix, or a single GTIN where your local GS1 office sells them.</li>
            <li>Choose UPC-A for a 12-digit GTIN or EAN-13 for a 13-digit one, and type the number.</li>
            <li>Place the SVG on the artwork at full size and scan a printed proof before the production run.</li>
          </ol>
          <p>The <Link to="/barcode-guides/what-is-a-gtin">guide to GTINs</Link> explains how the numbers are built.</p>
          <h3>Which Barcode to Use</h3>
          <ul>
            <li>UPC-A (GTIN-12) for retail products, mainly in the US and Canada.</li>
            <li>EAN-13 (GTIN-13) for retail products in most other countries.</li>
            <li>ITF-14 (GTIN-14) for cartons that do not pass through a checkout.</li>
            <li>Code 128 for shipping labels, asset tags and stock locations.</li>
          </ul>
          <p>Used as a UPC barcode generator, this page adds the check digit when you type 11 digits. For values with letters, see the <Link to="/barcode-guides/code-128-explained">Code 128 guide</Link>.</p>
          <h3>Can I Create My Own Barcodes?</h3>
          <p>Yes. Any value you make up can be printed as a Code 128 or Code 39 barcode for use inside your own business, with no registration.</p>
          <p>Retail barcodes need a GTIN licensed to your company. Any free barcode generator online can draw the symbol, but only GS1 issues the number.</p>
          <h2>How to Print a Barcode That Scans</h2>
          <p>A barcode scans reliably when printed at a supported size, as dark bars on a light background, with its quiet zones intact.</p>
          <ol>
            <li>Click Print and choose a single label, a grid or an Avery label sheet.</li>
            <li>Set the barcode width in millimeters.</li>
            <li>Print at 100% scale, not fit to page.</li>
          </ol>
          <p>For EAN-13 and UPC-A on retail goods, GS1 allows 80% to 200% of the nominal size, a narrow-bar width (X-dimension) of 0.264 mm to 0.660 mm. At 100% an EAN-13 is 37.29 mm wide, including its quiet zones.</p>
          <h3>Online Barcode Generator Settings for Printing</h3>
          <ul>
            <li>Scale (1x to 8x) gives the PNG more pixels; the SVG stays sharp at any scale.</li>
            <li>Bar height (5 to 40 mm) sets how tall the bars are drawn.</li>
            <li>Show value under barcode adds the human-readable digits that GS1 requires on EAN and UPC symbols.</li>
          </ul>
          <p>Bars are always black on white. For a long list of values, use the <Link to="/bulk-barcode-generator">bulk barcode generator</Link>.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="format">Format</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as FormatId)}>
                <SelectTrigger id="format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{def.hint}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">Data</Label>
              <Input
                id="value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={def.placeholder}
                spellCheck={false}
                autoComplete="off"
              />
              {encodedValue !== value.trim() && !error && (
                <p className="text-xs text-muted-foreground">
                  Encoded as <span className="font-mono">{encodedValue}</span> (check digit added).
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Scale</Label>
                <span className="text-xs text-muted-foreground">{scale}x</span>
              </div>
              <Slider
                min={1}
                max={8}
                step={1}
                value={[scale]}
                onValueChange={(v) => setScale(v[0])}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bar height</Label>
                <span className="text-xs text-muted-foreground">{height} mm</span>
              </div>
              <Slider
                min={5}
                max={40}
                step={1}
                value={[height]}
                onValueChange={(v) => setHeight(v[0])}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="text" className="cursor-pointer">
                Show value under barcode
              </Label>
              <Switch id="text" checked={includeText} onCheckedChange={setIncludeText} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[220px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[260px] max-w-full"
                  aria-label={`${def.label} barcode preview`}
                />
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <SupportedFormats />
      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`${def.label} · ${encodedValue.slice(0, 60) || "label"}`}
        filename={safeFilename(format, encodedValue)}
        svgMarkup={svgMarkup}
        defaultCaption={encodedValue}
        defaults={{
          layoutKind: "single",
          widthMm: 60,
          sheetPreset: "averyL7163",
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function SupportedFormats() {
  const formats = ["UPC-A", "EAN-13", "EAN-8", "Code 128", "Code 39", "ITF-14", "Codabar"];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Supported Barcode Formats</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Seven of the most common 1D symbologies used in retail, logistics, libraries and healthcare.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {formats.map((f) => (
          <Badge key={f} variant="outline" className="px-3 py-1 text-sm">
            {f}
          </Badge>
        ))}
      </div>
    </section>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        Barcodes are rendered locally in your browser with the bwip-js library. The value you
        enter and the PNG or SVG you download never touch our servers.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan a barcode you just generated to verify it decodes correctly.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits before printing labels.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the mod-10 check digit for a UPC, EAN or GTIN.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Identify the likely barcode format from a raw value.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode formats guide",
      desc: "Field guide to common 1D and 2D barcode symbologies.",
    },
    {
      path: "/bulk-barcode-generator",
      label: "Bulk Barcode Generator",
      desc: "Paste a list of values and generate every barcode on one printable sheet.",
    },
    {
      path: "/barcode-guides/what-is-a-gtin",
      label: "What is a GTIN?",
      desc: "How GTIN-8, 12, 13 and 14 numbers are structured and issued by GS1.",
    },
    {
      path: "/barcode-guides/code-128-explained",
      label: "Code 128 explained",
      desc: "Code sets, check character and when to choose Code 128 for labels.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/barcode-generator")({
  component: BarcodeGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Online Barcode Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
